import { useEffect, useRef } from 'react';
import { Autocomplete, TextField } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';

import { useTalentCommittees } from '@/queries/useTalentCommittees';
import type { TalentCommittee } from '@/api/types/talentCommittee.types';

interface TcSelectorProps {
  selectedTcId: number | null;
  onSelect: (tcId: number | null) => void;
}

export function TcSelector({ selectedTcId, onSelect }: TcSelectorProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { data: talentCommittees, isLoading } = useTalentCommittees();

  const tcs = talentCommittees ?? [];
  const selectedTc = tcs.find((tc) => tc.id === selectedTcId) ?? null;

  // Auto-select from navigation state (coming from TalentCommitteesPage), but only
  // once per mount. Without this guard, clearing the Autocomplete sets selectedTcId
  // to null and the effect re-fires, re-selecting the TC from the stale location
  // state — the "x" button would appear to do nothing.
  const hasConsumedNavStateRef = useRef(false);
  useEffect(() => {
    if (hasConsumedNavStateRef.current) return;
    const state = location.state as { tcId?: number } | null;
    if (state?.tcId && !selectedTcId) {
      hasConsumedNavStateRef.current = true;
      onSelect(state.tcId);
      // Replace the history entry to drop the navigation state, so a manual reload
      // doesn't re-trigger the auto-select.
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [location.state, location.pathname, selectedTcId, onSelect, navigate]);

  return (
    <Autocomplete
      size="small"
      sx={{ width: 280 }}
      options={tcs}
      value={selectedTc}
      loading={isLoading}
      getOptionLabel={(option: TalentCommittee) => option.name}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      onChange={(_, newValue) => onSelect(newValue?.id ?? null)}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Committee"
          variant="standard"
          aria-label="Select Talent Committee"
        />
      )}
    />
  );
}
